import { createAsyncThunk } from "@reduxjs/toolkit";
import * as EventServiceApi from "apis/EventServiceApi";
import * as UserServiceApi from "apis/UserServiceApi";

import { ThunkAPI } from "store/types";
import {
  setAttendingEvents,
  setCurrentAttendingEventsPage,
  setFirstAttendingEventsPage,
} from "./attendingEvents.slice";
import {
  AttendingEvent,
  AttendingEventStatusParams,
  AttendingEventType,
  FetchAttendingEventsParams,
} from "./attendingEvents.types";

export const fetchAttendingEvents = createAsyncThunk<
  void,
  FetchAttendingEventsParams,
  ThunkAPI
>("attendingEvents/fetchAttendingEvents", async (params, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const { data } = state.attendingEvents;
    const response = await EventServiceApi.getAttendingEvents(params);
    if (response.pagination.current_page === 1) {
      thunkAPI.dispatch(setFirstAttendingEventsPage(response.data));
    }
    thunkAPI.dispatch(setCurrentAttendingEventsPage(response));
    // Replace the events of the fetched page in the global data array
    const startIdx =
      (response.pagination.current_page - 1) * response.pagination.limit;
    const newData = [...data];
    response.data.forEach((event: AttendingEvent, idx: number) => {
      newData[startIdx + idx] = event;
    });
    thunkAPI.dispatch(setAttendingEvents(newData));
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } } };
    return thunkAPI.rejectWithValue(
      err.response?.data?.error || "Failed to fetch attending events"
    );
  }
});

export const setAttendingEventStatus = createAsyncThunk<
  void,
  AttendingEventStatusParams,
  ThunkAPI
>("attendingEvents/setAttendingEventStatus", async (params, thunkAPI) => {
  try {
    await UserServiceApi.setStatusData(params);
    // Refetch only the page where the updated event is
    const state = thunkAPI.getState();
    const { data, currentPage } = state.attendingEvents;
    const limit = currentPage.pagination.limit;
    // Find the index of the updated event
    const eventIndex = data.findIndex(
      (event) => event.id === params.activityId
    );
    // Calculate the page (1-based)
    const page =
      eventIndex >= 0
        ? Math.floor(eventIndex / limit) + 1
        : currentPage.pagination.current_page;
    thunkAPI.dispatch(
      fetchAttendingEvents({
        page,
        limit,
      })
    );
    // If the updated event is in page 1, also refresh firstPage
    if (page === 1) {
      thunkAPI.dispatch(fetchAttendingEvents({ page: 1, limit }));
    }
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } } };
    return thunkAPI.rejectWithValue(
      err.response?.data?.error || "Failed to set attending event status"
    );
  }
});

export const confirmEventAttendance = createAsyncThunk<
  void,
  { activityId: number },
  ThunkAPI
>("attendingEvents/confirmEventAttendance", async ({ activityId }, thunkAPI) => {
  try {
    await UserServiceApi.setStatusData({
      activityId,
      status: AttendingEventType.CONFIRMED
    });
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } } };
    return thunkAPI.rejectWithValue(
      err.response?.data?.error || "Failed to confirm event attendance"
    );
  }
});

export const requestEventParticipation = createAsyncThunk(
  "attendingEvents/requestParticipation",
  async ({ activityId }: { activityId: number }, thunkAPI) => {
    try {
      const response = await EventServiceApi.requestParticipation({ activity_id: activityId });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
